import React from 'react';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';

export const required = value => (value !== undefined && value !== null && value.toString() !== '' ? undefined : messages.required());
export const birthdayRequired = value => (value && value.day && value.month && value.year ? undefined : messages.required());
export const none = value => undefined;

export const validABA = value => (value && normalizeABA(value).length === 9 ? undefined : messages.exactLength(9));

export const validPostcode = (country, value) => {
    switch (country) {
        case 'CA':
            return validPostcodeCA(value);
        case 'UK':
            return validPostcodeUK(value);
        case 'US':
            return validPostcodeUS(value);
        default:
            return undefined;
    }
};
export const validPostcodeUS = value => (value && normalizePostcodeUS(value).length === 5 ? undefined : messages.zip());
export const validPostcodeCA = value => (value && normalizePostcodeCA(value).length === 7 ? undefined : messages.postcode());
export const validPostcodeUK = value => {
    if (!value) return messages.postcode();
    const normal = normalizePostcodeUK(value);
    if (normal.length < 5 || normal.length > 8) return messages.postcode();
    const spaces = normal.replace(/[^ ]/g, '');
    if (spaces.length > 1) return messages.postcode();
    return undefined;
};

export const maxLength = max => value => (value && value.length > max ? messages.maxLength(max) : undefined);
export const minLength = min => value => (value && value.length < min ? messages.minLength(min) : undefined);
export const minLength1 = minLength(1);
export const minLength8 = minLength(8);
export const password = [
    value => (value && !/[a-z]/.test(value) ? messages.password.lowercase() : undefined),
    value => (value && !/[A-Z]/.test(value) ? messages.password.uppercase() : undefined),
    value => (value && !/[0-9]/.test(value) ? messages.password.number() : undefined),
    value => (value && !/[_$*.[^{}()?\-"!@%&/,><':;|~`\]\\]/.test(value) ? messages.password.special() : undefined),
    minLength8
];
export const minValue = min => value => (value && value < min ? messages.minValue(min) : undefined);
export const maxValue = max => value => (value && value > max ? messages.maxValue(max) : undefined);
export const email = value => (value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value) ? messages.email() : undefined);
export const alphaNumeric = value => (value && /[^a-zA-Z0-9 ]/i.test(value) ? messages.alphaNumeric() : undefined);
export const alphabetic = value => (value && /[^a-zA-Z ]/i.test(value) ? messages.alphabetic() : undefined);
export const number = value => (value && Number.isNaN(Number(value)) ? messages.number() : undefined);
export const isValidDateTime = value =>
    !value || moment(moment(new Date(value)).format('MM/DD/YYYY HH:mm:ss'), 'MM/DD/YYYY HH:mm:ss', true).isValid() ? undefined : messages.date();
export const matchPasswords = values => {
    const errors = {};
    if (values.password && values.password2 && values.password !== values.password2) {
        errors.password2 = messages.passwordMismatch();
    }
    return errors;
};
export const poExtend = values => {
    const errors = {};
    if ((values.po_value && values.po_number) || (!values.po_value && !values.po_number)) {
        return errors;
    }

    errors.po_number = messages.poInvalid();
    errors.po_value = messages.poInvalid();

    return errors;
};

export const normalizePostcode = (country, value) => {
    switch (country) {
        case 'CA':
            return normalizePostcodeCA(value);

        case 'UK':
            return normalizePostcodeUK(value);

        case 'US':
            return normalizePostcodeUS(value);

        default:
            return value;
    }
};
export const normalizePostcodeUS = value => value && value.replace(/[^0-9-]/g, '').slice(0, 5);
export const normalizePostcodeCA = value => {
    if (!value) return value;
    const full = value
        .toUpperCase()
        .replace(/[^0-9A-Z ]/g, '')
        .slice(0, 7);
    return full;
};
export const normalizePostcodeUK = value =>
    value &&
    value
        .toUpperCase()
        .replace(/[^0-9A-Z ]/g, '')
        .slice(0, 8);

export const normalizeABA = value => value && value.replace(/[^0-9]/g, '').slice(0, 9);
export const normalizeAccount = value => value && value.replace(/[^0-9-]/g, '').slice(0, 19);
export const normalizeAlphabetic = value => value && value.replace(/[^a-zA-Z]/g, '');
export const normalizeYear = value => value && value.replace(/[^0-9]/g, '').slice(0, 4);

const messages = {
    required: () => <FormattedMessage id={'error.required'} defaultMessage={'Required'} />,
    zip: () => <FormattedMessage id={'error.zip'} defaultMessage={'5-digit ZIP'} />,
    postcode: () => <FormattedMessage id={'error.postcode'} defaultMessage={'Postcode'} />,
    exactLength: length => <FormattedMessage id={'error.exactLength'} values={{ length: length }} defaultMessage={'Must be {length} characters'} />,
    maxLength: length => <FormattedMessage id={'error.maxLength'} values={{ length: length }} defaultMessage={'Must be {length} characters or less'} />,
    minLength: length => <FormattedMessage id={'error.minLength'} values={{ length: length }} defaultMessage={'Must be {length} characters or more'} />,
    minValue: value => <FormattedMessage id={'error.minValue'} values={{ value: value }} defaultMessage={'Must be at least {value}'} />,
    maxValue: value => <FormattedMessage id={'error.maxValue'} values={{ value: value }} defaultMessage={'Cannot be more then {value}'} />,
    email: () => <FormattedMessage id={'error.invalidEmail'} defaultMessage={'Invalid email address'} />,
    alphaNumeric: () => <FormattedMessage id={'error.onlyAlphanumeric'} defaultMessage={'Only alphanumeric characters are supported'} />,
    alphabetic: () => <FormattedMessage id={'error.onlyAlphabetic'} defaultMessage={'Only alphabetic characters are supported'} />,
    number: () => <FormattedMessage id={'error.onlyNumbers'} defaultMessage={'Must be a number'} />,
    date: () => <FormattedMessage id={'error.invalidDate'} defaultMessage={'Invalid date/time format'} />,
    password: {
        lowercase: () => <FormattedMessage id={'error.passwordLowercase'} defaultMessage={'Password must include lowercase'} />,
        uppercase: () => <FormattedMessage id={'error.passwordUppercase'} defaultMessage={'Password must include UPPERCASE'} />,
        number: () => <FormattedMessage id={'error.passwordNumber'} defaultMessage={'Password must include a number'} />,
        special: () => <FormattedMessage id={'error.passwordSpecial'} defaultMessage={'Password must include a special character'} />
    },
    passwordMismatch: () => [<FormattedMessage key={'error.passwordMismatch'} id={'error.passwordMismatch'} defaultMessage={'Passwords do not match'} />],
    poInvalid: () => [
        <FormattedMessage key={'error.poInvalid'} id={'error.poInvalid'} defaultMessage={'Please enter both Purchase order fields, or clear both'} />
    ]
};

export const errorMessages = messages;



// WEBPACK FOOTER //
// ./src/utilities/validation.jsx